import { ReactNode, useContext, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import * as R from 'ramda';

import { Modal, Snackbar } from '@atom/mui';
import { CsvResponse } from '@atom/types/csv';
import { CsvErrorItem } from '@atom/types/error';
import api from '@atom/utilities/api';
import {
  BudgetTemplateType,
  getUploadBudgetEndpoint,
} from '@atom/utilities/endpoints';

import BudgetDetailContext from '../BudgetDetailContext';

import BudgetUploadMain from './BudgetUploadMain';

export interface Props {
  onClose: () => void;
  type: BudgetTemplateType;
}

export enum BudgetUploadView {
  MAIN,
  PENDING_CHANGES,
  PROCESSING,
}

const getUploadErrorMessage = (err: AxiosError<CsvResponse>) => {
  if (!err?.response?.data) {
    return 'Upload Failed.';
  }

  const { processedRows, failedRows, totalRows } = err.response.data;

  return R.isNil(processedRows) || R.isNil(failedRows) || R.isNil(totalRows)
    ? 'Upload Failed.'
    : `Successfully updated row 1 to ${processedRows}. Failed updating row ${totalRows - failedRows + 1} to ${totalRows}.`;
};

const BudgetUploadModal = ({ onClose, type }: Props) => {
  const { budget, parentBudgetUnit, fetchParentUnit } =
    useContext(BudgetDetailContext);

  const [errors, setErrors] = useState<CsvErrorItem[]>([]);
  const [validating, setValidating] = useState<boolean>(false);
  const [file, setFile] = useState<File>(null);
  const [view, setView] = useState(BudgetUploadView.MAIN);

  const resetState = () => {
    setErrors([]);
    setValidating(false);
    setFile(null);
    setView(BudgetUploadView.MAIN);
  };

  const handleValidationError = (err: AxiosError<CsvResponse>) => {
    const fallbackMsg =
      'Something went wrong. You may have reached a limit with the number of rows and columns in your file. Reducing them may resolve the issue.';

    const errs = R.pathOr<CsvErrorItem[]>(
      [{ message: fallbackMsg }],
      ['response', 'data', 'errors'],
      err,
    );

    setValidating(false);
    setErrors(errs);
  };

  const handleValidationResponse = async (response: CsvResponse) => {
    setView(BudgetUploadView.PENDING_CHANGES);
    setValidating(false);
  };

  const handleApplicationError = (err: AxiosError<CsvResponse>) => {
    const message = getUploadErrorMessage(err);

    Snackbar.error({
      message,
      action: 'Try Again',
      autoHideDuration: 10000,
      onActionClick: () => {
        setView(BudgetUploadView.MAIN);
      },
      onDismiss: () => {
        resetState();
      },
    });
  };

  const handleApplicationResponse = (response: CsvResponse) => {
    resetState();
    onClose();
    const message = `Successfully updated ${response.processedRows} inventory items.`;
    Snackbar.info({ message });
    fetchParentUnit({ budgetUnitId: parentBudgetUnit?.id });
  };

  const handleSubmit = async (validateOnly: boolean) => {
    if (validateOnly) {
      setValidating(true);
    } else {
      setView(BudgetUploadView.PROCESSING);

      Snackbar.info({
        message: 'Upload in progress...',
        disableClickAwayDismiss: true,
        autoHideDuration: null,
      });
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post<CsvResponse>(
        getUploadBudgetEndpoint(type),
        formData,
        {
          budgetUnitId: parentBudgetUnit?.id,
          budgetId: budget?.id,
          validateOnly,
        },
      );

      if (validateOnly) {
        await handleValidationResponse(data);
      } else {
        handleApplicationResponse(data);
      }
    } catch (error) {
      if (validateOnly) {
        handleValidationError(error);
      } else {
        handleApplicationError(error);
      }
    }
  };

  const handleConfirm = () => {
    const validateOnly = view !== BudgetUploadView.PENDING_CHANGES;
    handleSubmit(validateOnly);
  };

  const mainTitle =
    type === 'authorizedBudget'
      ? 'Upload Authorized Amounts'
      : 'Upload Budget Items';

  const titles = useMemo<{ [key in BudgetUploadView]: React.ReactNode }>(
    () => ({
      [BudgetUploadView.MAIN]: mainTitle,
      [BudgetUploadView.PENDING_CHANGES]: mainTitle,
      [BudgetUploadView.PROCESSING]: 'Upload in Progress...',
    }),
    [],
  );

  const onFileChange = (newFile: File) => {
    setFile(newFile);
    setErrors([]);
  };

  const content: { [key in BudgetUploadView]: ReactNode } = {
    [BudgetUploadView.MAIN]: (
      <BudgetUploadMain
        type={type}
        file={file}
        onFileChange={onFileChange}
        validating={validating}
        errors={errors}
        resetState={resetState}
      />
    ),
    [BudgetUploadView.PENDING_CHANGES]:
      'The values in your CSV will replace existing values in this budget. Are you sure?',
    [BudgetUploadView.PROCESSING]:
      "This may take a few minutes. Please don't press the Back button or exit your browser while in progress. We will let you know when it's complete.",
  };
  return (
    <Modal
      title={titles[view]}
      open
      onCancel={onClose}
      confirmButtonText="Upload"
      onConfirm={handleConfirm}
      onExited={onClose}
      data-cy="editBudgetModal"
      width="sm"
    >
      {content[view]}
    </Modal>
  );
};

export default BudgetUploadModal;
